import React, { useState, useEffect, useCallback } from 'react';
import api from '../../utils/api';
import Header from '../../components/Header';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Textarea } from '../../components/ui/textarea';
import { Badge } from '../../components/ui/badge';
import { ScrollArea } from '../../components/ui/scroll-area';
import { DATA_REFRESH_EVENT } from '../../contexts/WebSocketContext';
import { 
  FileText, Clock, CheckCircle, AlertCircle, Send, Upload, Download, 
  X, Plus, File, FileImage, FileArchive, Calendar, Award, RefreshCcw
} from 'lucide-react';
import { toast } from 'sonner';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../../components/ui/dialog';

const HomeworkPage = ({ onMenuClick }) => {
  const [homeworks, setHomeworks] = useState([]);
  const [selectedHomework, setSelectedHomework] = useState(null);
  const [submitContent, setSubmitContent] = useState('');
  const [selectedFiles, setSelectedFiles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [filter, setFilter] = useState('all'); // all, pending, submitted, graded

  const fetchHomework = useCallback(async () => {
    try {
      const response = await api.get('/homework');
      setHomeworks(response.data);
    } catch (error) {
      console.error('Error fetching homework:', error);
      toast.error('Erreur lors du chargement des devoirs');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchHomework();
  }, [fetchHomework]);

  // Listen for real-time updates
  useEffect(() => {
    const handleDataRefresh = (event) => {
      const { type } = event.detail;
      if (['homework_created', 'homework_graded'].includes(type)) {
        fetchHomework();
      }
    };

    window.addEventListener(DATA_REFRESH_EVENT, handleDataRefresh);
    return () => window.removeEventListener(DATA_REFRESH_EVENT, handleDataRefresh);
  }, [fetchHomework]);

  const handleFileChange = (e) => {
    const files = Array.from(e.target.files);
    const validFiles = files.filter(file => {
      const ext = file.name.split('.').pop().toLowerCase();
      const validExtensions = ['pdf', 'doc', 'docx', 'jpg', 'jpeg', 'png', 'gif', 'zip'];
      if (!validExtensions.includes(ext)) {
        toast.error(`Type de fichier non autorisé: ${file.name}`);
        return false;
      }
      if (file.size > 50 * 1024 * 1024) {
        toast.error(`Fichier trop volumineux: ${file.name} (max 50MB)`);
        return false;
      }
      return true;
    });
    setSelectedFiles(prev => [...prev, ...validFiles]);
  };

  const removeFile = (index) => {
    setSelectedFiles(prev => prev.filter((_, i) => i !== index));
  };

  const handleSubmit = async () => {
    if (!submitContent.trim() && selectedFiles.length === 0) {
      toast.error('Veuillez ajouter une réponse ou des fichiers');
      return;
    }

    setSubmitting(true);
    try {
      const formData = new FormData();
      if (submitContent.trim()) {
        formData.append('content', submitContent);
      }
      selectedFiles.forEach(file => {
        formData.append('files', file);
      });

      await api.post(`/homework/${selectedHomework.id}/submit`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      
      toast.success('Devoir soumis avec succès');
      setDialogOpen(false);
      setSubmitContent('');
      setSelectedFiles([]);
      fetchHomework();
    } catch (error) {
      console.error('Error submitting homework:', error);
      toast.error('Erreur lors de la soumission');
    } finally {
      setSubmitting(false);
    }
  };

  const getStatusBadge = (status) => {
    const variants = {
      not_started: { color: 'bg-slate-100 text-slate-700', icon: AlertCircle, text: 'Non commencé' },
      pending: { color: 'bg-amber-100 text-amber-700', icon: Clock, text: 'En cours' },
      submitted: { color: 'bg-blue-100 text-blue-700', icon: Clock, text: 'Soumis' },
      graded: { color: 'bg-emerald-100 text-emerald-700', icon: CheckCircle, text: 'Corrigé' }
    };
    const config = variants[status] || variants.not_started;
    const Icon = config.icon;
    return (
      <Badge className={`${config.color} flex items-center gap-1`}>
        <Icon className="w-3 h-3" />
        {config.text}
      </Badge>
    );
  };

  const getFileIcon = (filename) => {
    const ext = filename.split('.').pop().toLowerCase();
    if (['jpg', 'jpeg', 'png', 'gif'].includes(ext)) return FileImage;
    if (['zip', 'rar'].includes(ext)) return FileArchive;
    return File;
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return 'Pas de date limite';
    const date = new Date(dateStr);
    return date.toLocaleDateString('fr-FR', { 
      day: 'numeric', 
      month: 'long', 
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const isOverdue = (dueDate) => {
    if (!dueDate) return false;
    return new Date(dueDate) < new Date();
  };

  // Filter homeworks
  const filteredHomeworks = homeworks.filter(hw => {
    if (filter === 'all') return true;
    if (filter === 'pending') return ['not_started', 'pending'].includes(hw.status);
    if (filter === 'submitted') return hw.status === 'submitted';
    if (filter === 'graded') return hw.status === 'graded';
    return true;
  });

  // Stats
  const stats = {
    total: homeworks.length,
    pending: homeworks.filter(h => ['not_started', 'pending'].includes(h.status)).length,
    submitted: homeworks.filter(h => h.status === 'submitted').length,
    graded: homeworks.filter(h => h.status === 'graded').length
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-violet-600"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50 dark:bg-slate-900">
      <Header title="Mes Devoirs" onMenuClick={onMenuClick} />
      
      <main className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
        {/* Stats */}
        <div className="grid grid-cols-4 gap-4 mb-6">
          <Card className="cursor-pointer hover:border-violet-300" onClick={() => setFilter('all')}>
            <CardContent className="p-4">
              <p className="text-2xl font-bold text-slate-900 dark:text-white">{stats.total}</p>
              <p className="text-xs text-slate-500">Total</p>
            </CardContent>
          </Card>
          <Card className="cursor-pointer hover:border-amber-300" onClick={() => setFilter('pending')}>
            <CardContent className="p-4">
              <p className="text-2xl font-bold text-amber-600">{stats.pending}</p>
              <p className="text-xs text-slate-500">À rendre</p>
            </CardContent>
          </Card>
          <Card className="cursor-pointer hover:border-blue-300" onClick={() => setFilter('submitted')}>
            <CardContent className="p-4">
              <p className="text-2xl font-bold text-blue-600">{stats.submitted}</p>
              <p className="text-xs text-slate-500">Soumis</p>
            </CardContent>
          </Card>
          <Card className="cursor-pointer hover:border-emerald-300" onClick={() => setFilter('graded')}>
            <CardContent className="p-4">
              <p className="text-2xl font-bold text-emerald-600">{stats.graded}</p>
              <p className="text-xs text-slate-500">Corrigés</p>
            </CardContent>
          </Card>
        </div>

        {/* Header */}
        <div className="flex items-center justify-between mb-6">
          <div>
            <h2 className="text-xl font-bold text-slate-900 dark:text-white">
              {filter === 'all' ? 'Tous les devoirs' : 
               filter === 'pending' ? 'Devoirs à rendre' :
               filter === 'submitted' ? 'Devoirs soumis' : 'Devoirs corrigés'}
            </h2>
            <p className="text-sm text-slate-500">{filteredHomeworks.length} devoir(s)</p>
          </div>
          <Button variant="outline" onClick={fetchHomework}>
            <RefreshCcw className="w-4 h-4 mr-2" />
            Actualiser
          </Button>
        </div>

        {/* Homework List */}
        {filteredHomeworks.length === 0 ? (
          <Card>
            <CardContent className="py-12 text-center">
              <FileText className="w-12 h-12 mx-auto mb-4 text-slate-300" />
              <p className="text-slate-500">Aucun devoir dans cette catégorie</p>
            </CardContent>
          </Card>
        ) : (
          <div className="grid gap-4">
            {filteredHomeworks.map((homework) => (
              <Card 
                key={homework.id} 
                className={`hover:shadow-md transition-shadow ${
                  isOverdue(homework.due_date) && homework.status === 'not_started' 
                    ? 'border-red-300 bg-red-50/50' 
                    : ''
                }`}
                data-testid={`homework-${homework.id}`}
              >
                <CardContent className="p-5">
                  <div className="flex items-start justify-between gap-4">
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-3 mb-2">
                        <h3 className="font-semibold text-slate-900 dark:text-white">{homework.title}</h3>
                        {getStatusBadge(homework.status)}
                        {homework.score !== null && (
                          <Badge className="bg-violet-100 text-violet-700">
                            <Award className="w-3 h-3 mr-1" />
                            {homework.score}/{homework.max_score || 100}
                          </Badge>
                        )}
                      </div>
                      
                      <p className="text-sm text-slate-600 dark:text-slate-400 mb-3 line-clamp-2">
                        {homework.description || 'Pas de description'}
                      </p>
                      
                      <div className="flex items-center gap-4 text-xs text-slate-500 mb-3">
                        <span className={`flex items-center gap-1 ${
                          isOverdue(homework.due_date) && homework.status === 'not_started' 
                            ? 'text-red-600 font-medium' 
                            : ''
                        }`}>
                          <Calendar className="w-3 h-3" />
                          Échéance: {formatDate(homework.due_date)}
                        </span>
                      </div>

                      {/* Fichiers joints par le professeur */}
                      {homework.attached_files && homework.attached_files.length > 0 && (
                        <div className="mb-3">
                          <p className="text-xs font-medium text-slate-600 mb-2">Fichiers du professeur :</p>
                          <div className="flex flex-wrap gap-2">
                            {homework.attached_files.map((file, idx) => {
                              const FileIcon = getFileIcon(file.name);
                              return (
                                <a
                                  key={idx}
                                  href={`${process.env.REACT_APP_BACKEND_URL}/api${file.url}`}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                  className="inline-flex items-center gap-1 px-2 py-1 bg-blue-50 text-blue-700 rounded-md text-xs hover:bg-blue-100 transition-colors"
                                >
                                  <FileIcon className="w-3 h-3" />
                                  <Download className="w-3 h-3" />
                                  {file.name}
                                </a>
                              );
                            })}
                          </div>
                        </div>
                      )}

                      {/* Mes fichiers soumis */}
                      {homework.my_files && homework.my_files.length > 0 && (
                        <div className="mb-3">
                          <p className="text-xs font-medium text-slate-600 mb-2">Mes fichiers soumis :</p>
                          <div className="flex flex-wrap gap-2">
                            {homework.my_files.map((file, idx) => {
                              const FileIcon = getFileIcon(file.name);
                              return (
                                <span
                                  key={idx}
                                  className="inline-flex items-center gap-1 px-2 py-1 bg-emerald-50 text-emerald-700 rounded-md text-xs"
                                >
                                  <FileIcon className="w-3 h-3" />
                                  {file.name}
                                </span>
                              );
                            })}
                          </div>
                        </div>
                      )}

                      {/* Feedback du professeur */}
                      {homework.feedback && (
                        <div className="p-3 bg-violet-50 dark:bg-violet-900/20 rounded-lg border border-violet-200 dark:border-violet-800">
                          <p className="text-xs font-medium text-violet-700 dark:text-violet-400 mb-1">
                            Commentaire du professeur :
                          </p>
                          <p className="text-sm text-slate-700 dark:text-slate-300">{homework.feedback}</p>
                        </div>
                      )}
                    </div>

                    {/* Actions */}
                    <div className="flex flex-col gap-2">
                      {homework.status !== 'graded' && (
                        <Button 
                          onClick={() => {
                            setSelectedHomework(homework);
                            setSubmitContent('');
                            setSelectedFiles([]);
                            setDialogOpen(true);
                          }}
                          className="whitespace-nowrap"
                          data-testid={`submit-btn-${homework.id}`}
                        >
                          <Send className="w-4 h-4 mr-2" />
                          {homework.status === 'submitted' ? 'Modifier' : 'Rendre'}
                        </Button>
                      )}
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        )}
      </main>

      {/* Submit Dialog */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>Rendre le devoir</DialogTitle>
            <DialogDescription>
              {selectedHomework?.title}
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4 py-4">
            {/* Fichiers du professeur à télécharger */}
            {selectedHomework?.attached_files && selectedHomework.attached_files.length > 0 && (
              <div className="p-3 bg-blue-50 dark:bg-blue-900/20 rounded-lg">
                <p className="text-sm font-medium text-blue-700 dark:text-blue-400 mb-2">
                  Fichiers du professeur à télécharger :
                </p>
                <div className="flex flex-wrap gap-2">
                  {selectedHomework.attached_files.map((file, idx) => (
                    <a
                      key={idx}
                      href={`${process.env.REACT_APP_BACKEND_URL}/api${file.url}`}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="inline-flex items-center gap-1 px-3 py-2 bg-white text-blue-700 rounded-md text-sm border border-blue-200 hover:bg-blue-50 transition-colors"
                    >
                      <Download className="w-4 h-4" />
                      {file.name}
                    </a>
                  ))}
                </div>
              </div>
            )}

            {/* Réponse texte */}
            <div>
              <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1">
                Votre réponse (optionnel)
              </label>
              <Textarea
                value={submitContent}
                onChange={(e) => setSubmitContent(e.target.value)}
                placeholder="Entrez votre réponse ici..."
                rows={4}
                data-testid="homework-content-input"
              />
            </div>

            {/* Upload fichiers */}
            <div>
              <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1">
                Fichiers joints
              </label>
              <p className="text-xs text-slate-500 mb-2">
                Formats acceptés : PDF, DOC, DOCX, images (JPG, PNG), ZIP (max 50MB)
              </p>
              
              <div className="border-2 border-dashed border-slate-300 dark:border-slate-600 rounded-lg p-4 text-center hover:border-violet-400 transition-colors">
                <input
                  type="file"
                  multiple
                  onChange={handleFileChange}
                  className="hidden"
                  id="file-upload"
                  accept=".pdf,.doc,.docx,.jpg,.jpeg,.png,.gif,.zip"
                  data-testid="file-upload-input"
                />
                <label htmlFor="file-upload" className="cursor-pointer">
                  <Upload className="w-8 h-8 mx-auto mb-2 text-slate-400" />
                  <p className="text-sm text-slate-600 dark:text-slate-400">
                    Cliquez pour ajouter des fichiers
                  </p>
                </label>
              </div>

              {/* Liste des fichiers sélectionnés */}
              {selectedFiles.length > 0 && (
                <div className="mt-3 space-y-2">
                  {selectedFiles.map((file, index) => {
                    const FileIcon = getFileIcon(file.name);
                    return (
                      <div
                        key={index}
                        className="flex items-center justify-between p-2 bg-slate-100 dark:bg-slate-800 rounded-md"
                      >
                        <div className="flex items-center gap-2">
                          <FileIcon className="w-4 h-4 text-slate-500" />
                          <span className="text-sm text-slate-700 dark:text-slate-300 truncate max-w-[200px]">
                            {file.name}
                          </span>
                          <span className="text-xs text-slate-500">
                            ({(file.size / 1024 / 1024).toFixed(2)} MB)
                          </span>
                        </div>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => removeFile(index)}
                          className="text-red-500 hover:text-red-700"
                        >
                          <X className="w-4 h-4" />
                        </Button>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setDialogOpen(false)}>
              Annuler
            </Button>
            <Button 
              onClick={handleSubmit} 
              disabled={submitting || (!submitContent.trim() && selectedFiles.length === 0)}
              data-testid="submit-homework-btn"
            >
              {submitting ? 'Envoi en cours...' : 'Soumettre'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default HomeworkPage;
